import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Factory, Wrench, Zap, Shield, Globe, Award, Users, CheckCircle, TrendingDown, Target, ChevronRight, DollarSign, Building, Truck, Settings, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import AnimatedSection, { CountUpNumber, StaggeredContainer } from '../components/AnimatedSection';
import heroImage from '../assets/hero/hero-main.jpg';
import companyImage from '../assets/factory/factory-exterior.png';
import { t } from '../utils/translation';
import SectionSplitter from '../components/SectionSplitter';
import { useSiteAssets } from '../context/SiteAssetsContext';

// Animated number counter
const AnimatedNumber = ({ value, isVisible, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const end = typeof value === 'number' ? value : parseFloat(value) || 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) { clearInterval(timer); setCount(end); }
      else { setCount(Math.ceil(start)); }
    }, 16);
    return () => clearInterval(timer);
  }, [isVisible, value, duration]);
  return <>{typeof value === 'number' ? count : value}</>;
};

const useScrollAnimation = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); }
    }, { threshold });
    if (elementRef.current) observer.observe(elementRef.current);
    return () => { if (elementRef.current) observer.unobserve(elementRef.current); };
  }, [threshold]);
  return [elementRef, isVisible];
};

const Home = ({ theme, language }) => {
  const { assets } = useSiteAssets();
  const isDark = theme === 'dark';
  const [statsRef, statsVisible] = useScrollAnimation(0.2);

  const services = [
    {
      icon: <Factory className="w-10 h-10 text-red-600" />,
      title: t('manufacturing', language),
      desc: 'Full in-house production from injection molding and stamping to final assembly — ensuring consistent quality at every step.',
    },
    {
      icon: <Wrench className="w-10 h-10 text-red-600" />,
      title: t('customSolutions', language),
      desc: 'Custom connector designs built to your exact specifications and part numbers, with same-brand compatibility guaranteed.',
    },
    {
      icon: <Zap className="w-10 h-10 text-red-600" />,
      title: t('fastDelivery', language),
      desc: 'Typically 2–4 week lead times for production orders. Rapid sample prototypes available within days for design validation.',
    },
    {
      icon: <Shield className="w-10 h-10 text-red-600" />,
      title: t('qualityGuarantee', language),
      desc: 'ISO 9001:2015 certified. Every batch is electrically and mechanically tested to match or exceed OEM specifications.',
    },
  ];

  const stats = [
    { number: 14, label: 'Years of Experience', icon: <Award className="w-7 h-7" />, suffix: '+' },
    { number: 1000, label: 'Global Clients', icon: <Users className="w-7 h-7" />, suffix: '+' },
    { number: 8, label: 'Countries Served', icon: <Globe className="w-7 h-7" />, suffix: '+' },
    { number: 70, label: 'Cost Savings vs OEM', icon: <TrendingDown className="w-7 h-7" />, suffix: '%' },
  ];

  const valuePropositions = [
    {
      icon: <Building className="w-7 h-7 text-red-600" />,
      title: 'Vertically Integrated',
      description: 'We control injection molding, stamping, plating, and assembly in-house — no outsourcing means fewer markups and tighter quality control.',
    },
    {
      icon: <Settings className="w-7 h-7 text-red-600" />,
      title: 'Automated Manufacturing',
      description: 'Modern production lines and automated presses reduce labor costs and human error, keeping unit prices low without sacrificing precision.',
    },
    {
      icon: <Truck className="w-7 h-7 text-red-600" />,
      title: 'Direct-to-Buyer Sales',
      description: 'We ship direct from factory to you — no distributor, no middleman. The savings go straight to your bottom line.',
    },
    {
      icon: <DollarSign className="w-7 h-7 text-red-600" />,
      title: 'No Brand Premium',
      description: 'Same materials, same specs, same performance — without paying for the OEM brand name or licensing fees.',
    },
  ];

  const brands = [
    'TE', 'YAZAKI', 'SUMITOMO', 'DELPHI', 'KET', 'ERNI',
    'DEUTSCH', 'ITT', 'JST', 'AMPHENOL', 'MOLEX', 'HRS',
    'JAE', 'HARTING', 'SAMTEC', 'WAGO',
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 pt-20 ${isDark ? 'text-white' : 'text-gray-900'}`}>

      <SEO
        title="Save 50–70% on Precision Connector Alternatives | Qingshun Electronics"
        description="ISO-certified manufacturer of direct connector replacements for TE, Molex, Amphenol, JST & 20+ top brands. Same specs. Up to 70% less cost. Trusted by 1,000+ engineers worldwide."
        keywords="connector alternatives, TE connector replacement, Molex alternative, Amphenol connector, JST connector, custom connectors, automotive connectors, industrial connectors, cost saving electronics"
        url="/"
        image="/logo512.png"
        type="website"
        language={language}
        showLocalBusiness={true}
        faqs={[
          {
            question: 'How much can I save by switching to Qingshun connector alternatives?',
            answer: 'Most customers save 50–70% compared to OEM brands like TE, Molex, and Amphenol while receiving the exact same electrical and mechanical specifications.',
          },
          {
            question: 'Are Qingshun connectors direct drop-in replacements?',
            answer: 'Yes. Our connectors are engineered to be pin-compatible, dimensionally identical, and electrically equivalent to over 20 major brands.',
          },
          {
            question: 'What industries does Qingshun serve?',
            answer: 'We supply connectors for automotive, industrial automation, medical devices, consumer electronics, data centers, and telecommunications.',
          },
        ]}
      />

      {/* ── Hero ── */}
      <section className={`relative pt-10 pb-20 px-4 overflow-hidden ${isDark ? 'hero-gradient-dark' : 'hero-gradient-light'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">

            {/* Copy */}
            <div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 14px',
                borderRadius: 999,
                border: '1.5px solid rgba(220,38,38,0.35)',
                background: isDark ? 'rgba(220,38,38,0.1)' : 'rgba(220,38,38,0.07)',
                marginBottom: 20,
                fontSize: 12,
                fontWeight: 600,
                color: '#dc2626',
                letterSpacing: 0.5,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#dc2626', display: 'inline-block', animation: 'breathPulseGlobal 2s infinite' }} />
                ISO 9001:2015 Certified Manufacturer
              </div>

              <h1 style={{
                fontSize: 'clamp(2rem, 5vw, 3.75rem)',
                fontWeight: 800,
                lineHeight: 1.12,
                letterSpacing: '-0.03em',
                marginBottom: 20,
                color: isDark ? '#f87171' : '#b91c1c',
              }}>
                Save 50–70% on<br />
                <span style={{ color: isDark ? '#ef4444' : '#dc2626' }}>Precision Connector</span>
                <br />Alternatives
              </h1>

              <p style={{
                fontSize: 'clamp(1rem, 2vw, 1.15rem)',
                lineHeight: 1.75,
                marginBottom: 28,
                color: isDark ? '#d1d5db' : '#374151',
                maxWidth: 480,
              }}>
                Direct drop-in replacements for TE, Molex, Amphenol, JST & 20+ leading brands — manufactured in our ISO-certified Suzhou facility. Trusted by 1,000+ engineers in 8+ countries.
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 28 }}>
                <Link to="/contact"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '14px 28px', borderRadius: 14,
                    background: 'linear-gradient(135deg, #dc2626, #991b1b)',
                    color: '#fff', fontWeight: 700, fontSize: 15,
                    textDecoration: 'none',
                    boxShadow: '0 6px 24px rgba(220,38,38,0.35)',
                    transition: 'all 0.3s',
                    minHeight: 'unset', minWidth: 'unset',
                  }}>
                  Get a Free Quote
                  <ArrowRight size={16} />
                </Link>
                <Link to="/services"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '14px 24px', borderRadius: 14,
                    border: '2px solid rgba(220,38,38,0.4)',
                    color: isDark ? '#f87171' : '#dc2626',
                    fontWeight: 600, fontSize: 15,
                    textDecoration: 'none',
                    background: 'transparent',
                    transition: 'all 0.3s',
                    minHeight: 'unset', minWidth: 'unset',
                  }}>
                  View Services
                </Link>
              </div>

              {/* Trust badges */}
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 10,
              }}>
                {['ISO 9001', 'RoHS Compliant', '14+ Years', '1,000+ Clients'].map(badge => (
                  <span key={badge} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '5px 12px', borderRadius: 8,
                    background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
                    fontSize: 12, fontWeight: 600,
                    color: isDark ? '#9ca3af' : '#6b7280',
                  }}>
                    <CheckCircle size={11} style={{ color: '#22c55e' }} />
                    {badge}
                  </span>
                ))}
              </div>
            </div>

            {/* Hero Image */}
            <div style={{ position: 'relative' }}>
              <div style={{
                borderRadius: 20,
                overflow: 'hidden',
                border: isDark ? '2px solid rgba(220,38,38,0.3)' : '2px solid rgba(220,38,38,0.2)',
                boxShadow: isDark
                  ? '0 30px 80px rgba(0,0,0,0.6), 0 0 40px rgba(220,38,38,0.15)'
                  : '0 30px 80px rgba(0,0,0,0.12), 0 0 40px rgba(220,38,38,0.08)',
              }}>
                <img
                  src={assets.home_hero || heroImage}
                  alt="Premium Connector Alternatives — Qingshun Electronics Manufacturing, Suzhou China"
                  loading="eager"
                  fetchpriority="high"
                  style={{ width: '100%', height: 380, objectFit: 'cover', display: 'block' }}
                />
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.25) 0%, transparent 50%)',
                }} />
              </div>

              {/* Floating badge */}
              <div style={{
                position: 'absolute', bottom: -16, left: 24,
                padding: '12px 20px', borderRadius: 14,
                background: isDark ? 'rgba(0,0,0,0.9)' : '#fff',
                border: isDark ? '1px solid rgba(220,38,38,0.25)' : '1px solid rgba(220,38,38,0.15)',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                backdropFilter: 'blur(10px)',
              }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#dc2626', lineHeight: 1 }}>50–70%</div>
                <div style={{ fontSize: 11, color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500, marginTop: 2 }}>Cost savings vs OEM</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionSplitter theme={theme} variant="wave" />

      {/* ── Why We Cost Less ── */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection animation="fade-up" className="text-center mb-14">
            <h2 style={{
              fontSize: 'clamp(1.7rem, 4vw, 2.8rem)',
              fontWeight: 800, letterSpacing: '-0.02em',
              marginBottom: 14,
              color: isDark ? '#f87171' : '#b91c1c',
            }}>
              Why Our Connectors Cost Less
            </h2>
            <p style={{
              fontSize: '1.05rem', maxWidth: 560, margin: '0 auto',
              lineHeight: 1.75,
              color: isDark ? '#9ca3af' : '#6b7280',
            }}>
              We've eliminated every unnecessary cost in the supply chain — without cutting corners on the product itself.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {valuePropositions.map((vp, i) => (
              <div key={i} style={{
                borderRadius: 16, padding: 24,
                border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(220,38,38,0.1)',
                background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
                boxShadow: isDark ? 'none' : '0 4px 24px rgba(0,0,0,0.05)',
                transition: 'all 0.3s',
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: isDark ? 'rgba(220,38,38,0.12)' : 'rgba(220,38,38,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 14,
                }}>
                  {vp.icon}
                </div>
                <h3 style={{
                  fontSize: 16, fontWeight: 700, marginBottom: 8,
                  color: isDark ? '#f9fafb' : '#111827',
                }}>{vp.title}</h3>
                <p style={{
                  fontSize: 13.5, lineHeight: 1.65,
                  color: isDark ? '#9ca3af' : '#6b7280',
                  margin: 0,
                }}>{vp.description}</p>
              </div>
            ))}
          </div>

          {/* Savings callout */}
          <AnimatedSection animation="scale-up">
            <div style={{
              borderRadius: 20, padding: '36px 40px',
              textAlign: 'center',
              background: isDark
                ? 'linear-gradient(135deg, rgba(220,38,38,0.1), rgba(153,27,27,0.08))'
                : 'linear-gradient(135deg, #fff5f5, #fff)',
              border: isDark ? '1.5px solid rgba(220,38,38,0.25)' : '1.5px solid rgba(220,38,38,0.15)',
              boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.3)' : '0 10px 40px rgba(220,38,38,0.08)',
            }}>
              <TrendingDown style={{ width: 40, height: 40, color: '#dc2626', margin: '0 auto 14px' }} />
              <h3 style={{
                fontSize: 'clamp(1.4rem, 3vw, 2rem)',
                fontWeight: 800, marginBottom: 10,
                color: isDark ? '#f87171' : '#b91c1c',
              }}>
                Up to 70% Cost Reduction — No Compromise
              </h3>
              <p style={{
                fontSize: '1rem', lineHeight: 1.75,
                marginBottom: 24, maxWidth: 520, margin: '0 auto 24px',
                color: isDark ? '#d1d5db' : '#4b5563',
              }}>
                Our customers routinely cut their connector budget in half — with identical performance, tolerances, and compatibility to their original OEM parts.
              </p>
              <Link to="/contact" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '13px 28px', borderRadius: 12,
                background: 'linear-gradient(135deg, #dc2626, #991b1b)',
                color: '#fff', fontWeight: 700, fontSize: 15,
                textDecoration: 'none',
                boxShadow: '0 6px 20px rgba(220,38,38,0.3)',
                minHeight: 'unset', minWidth: 'unset',
              }}>
                Get a Quote
                <ArrowRight size={15} />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <SectionSplitter theme={theme} variant="diagonal" />

      {/* ── Services ── */}
      <section className={`relative py-20 px-4 overflow-hidden ${isDark ? 'section-alt-dark' : 'section-alt-light'}`}>
        <div className="max-w-7xl mx-auto relative z-10">
          <AnimatedSection animation="fade-up" className="text-center mb-14">
            <h2 style={{
              fontSize: 'clamp(1.7rem, 4vw, 2.8rem)',
              fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 12,
              color: isDark ? '#f87171' : '#b91c1c',
            }}>
              Our Manufacturing Services
            </h2>
            <p style={{
              fontSize: '1.05rem', maxWidth: 500, margin: '0 auto',
              lineHeight: 1.75, color: isDark ? '#9ca3af' : '#6b7280',
            }}>
              End-to-end connector solutions — from prototype to mass production, fully managed under one roof.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
            {services.map((service, i) => (
              <div key={i} style={{
                borderRadius: 18, overflow: 'hidden',
                border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(220,38,38,0.1)',
                background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
                padding: 24,
                transition: 'all 0.3s',
                boxShadow: isDark ? 'none' : '0 4px 20px rgba(0,0,0,0.05)',
              }}>
                <div style={{
                  width: 52, height: 52, borderRadius: 14,
                  background: isDark ? 'rgba(220,38,38,0.12)' : 'rgba(220,38,38,0.07)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 16,
                }}>
                  {service.icon}
                </div>
                <h3 style={{
                  fontSize: 16, fontWeight: 700, marginBottom: 8,
                  color: isDark ? '#f9fafb' : '#111827',
                }}>
                  {service.title}
                </h3>
                <p style={{
                  fontSize: 13.5, lineHeight: 1.65, margin: 0,
                  color: isDark ? '#9ca3af' : '#6b7280',
                }}>
                  {service.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Stats Banner */}
          <div ref={statsRef} style={{
            borderRadius: 20, padding: '32px 40px',
            background: isDark
              ? 'linear-gradient(135deg, rgba(220,38,38,0.08), rgba(0,0,0,0.5))'
              : 'linear-gradient(135deg, #fff, #fef2f2)',
            border: isDark ? '1px solid rgba(220,38,38,0.2)' : '1px solid rgba(220,38,38,0.12)',
            boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.3)' : '0 10px 40px rgba(220,38,38,0.07)',
          }}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{
                    color: '#dc2626', display: 'flex', justifyContent: 'center', marginBottom: 8,
                  }}>
                    {stat.icon}
                  </div>
                  <div style={{
                    fontSize: 'clamp(2rem, 5vw, 3rem)',
                    fontWeight: 800, lineHeight: 1,
                    color: isDark ? '#f87171' : '#dc2626',
                    fontVariantNumeric: 'tabular-nums',
                    marginBottom: 6,
                  }}>
                    <AnimatedNumber value={stat.number} isVisible={statsVisible} />
                    {stat.suffix}
                  </div>
                  <div style={{
                    fontSize: 13, fontWeight: 500,
                    color: isDark ? '#9ca3af' : '#6b7280',
                  }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SectionSplitter theme={theme} variant="curve" />

      {/* ── Brands We Replace ── */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{
              fontSize: 'clamp(1.7rem, 4vw, 2.8rem)',
              fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 12,
              color: isDark ? '#f87171' : '#b91c1c',
            }}>
              20+ Brands We Replace
            </h2>
            <p style={{
              fontSize: '1.05rem', maxWidth: 520, margin: '0 auto',
              lineHeight: 1.75, color: isDark ? '#9ca3af' : '#6b7280',
            }}>
              Direct pin-compatible replacements for the world's leading connector manufacturers — tested to the same specs, at a fraction of the price.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: 10,
            marginBottom: 36,
          }}>
            {brands.map((brand, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '14px 10px', borderRadius: 12,
                background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
                border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(220,38,38,0.1)',
                boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.04)',
                transition: 'all 0.2s',
                cursor: 'default',
              }}>
                <span style={{
                  fontWeight: 700, fontSize: 13, letterSpacing: 0.5,
                  color: isDark ? '#d1d5db' : '#374151',
                }}>
                  {brand}
                </span>
              </div>
            ))}
          </div>

          {/* Brand CTA Card */}
          <div style={{
            maxWidth: 640, margin: '0 auto',
            padding: '28px 32px', borderRadius: 18,
            background: isDark
              ? 'linear-gradient(135deg, rgba(220,38,38,0.1), rgba(0,0,0,0.5))'
              : 'linear-gradient(135deg, #fef2f2, #fff)',
            border: isDark ? '1.5px solid rgba(220,38,38,0.2)' : '1.5px solid rgba(220,38,38,0.15)',
            display: 'flex', flexWrap: 'wrap',
            alignItems: 'center', justifyContent: 'space-between', gap: 16,
          }}>
            <div>
              <p style={{
                fontWeight: 700, fontSize: 17, marginBottom: 4,
                color: isDark ? '#f9fafb' : '#111827',
              }}>
                Don't see your brand?
              </p>
              <p style={{
                fontSize: 13.5, color: isDark ? '#9ca3af' : '#6b7280', margin: 0,
              }}>
                We can match virtually any connector. Send us a part number.
              </p>
            </div>
            <Link to="/contact" style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '11px 20px', borderRadius: 12,
              background: 'linear-gradient(135deg, #dc2626, #991b1b)',
              color: '#fff', fontWeight: 700, fontSize: 14,
              textDecoration: 'none',
              boxShadow: '0 6px 20px rgba(220,38,38,0.28)',
              whiteSpace: 'nowrap',
              minHeight: 'unset', minWidth: 'unset',
            }}>
              Request Custom
              <ChevronRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      <SectionSplitter theme={theme} variant="slant" />

      {/* ── About Company ── */}
      <section className={`py-16 px-4 ${isDark ? 'section-alt-dark' : 'section-alt-light'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="slide-left" className="relative">
              <div style={{
                borderRadius: 20, overflow: 'hidden',
                border: isDark ? '2px solid rgba(220,38,38,0.25)' : '2px solid rgba(220,38,38,0.15)',
                boxShadow: isDark
                  ? '0 30px 80px rgba(0,0,0,0.5)'
                  : '0 20px 60px rgba(0,0,0,0.1)',
              }}>
                <img
                  src={assets.home_company || companyImage}
                  alt="Qingshun Electronics ISO-Certified Manufacturing Facility in Suzhou, China"
                  loading="lazy"
                  style={{ width: '100%', height: 340, objectFit: 'cover', display: 'block' }}
                />
              </div>
            </AnimatedSection>

            <AnimatedSection animation="slide-right">
              <h2 style={{
                fontSize: 'clamp(1.6rem, 4vw, 2.6rem)',
                fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 16,
                color: isDark ? '#f87171' : '#b91c1c',
              }}>
                A Factory You Can Trust
              </h2>
              <p style={{
                fontSize: '1rem', lineHeight: 1.8, marginBottom: 14,
                color: isDark ? '#d1d5db' : '#4b5563',
              }}>
                Founded in 2011, Suzhou Qingshun Electronics manufactures drop-in connector replacements for TE, Molex, Amphenol, JST, and 20+ brands — at 50–70% lower cost. ISO 9001:2015 certified since 2017.
              </p>
              <p style={{
                fontSize: '1rem', lineHeight: 1.8, marginBottom: 24,
                color: isDark ? '#d1d5db' : '#4b5563',
              }}>
                Our fully vertically integrated Suzhou factory covers injection molding, precision stamping, electroplating, and assembly under one roof — giving us total quality control and razor-thin margins that we pass on to you.
              </p>

              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 28,
              }}>
                {['ISO 9001:2015 Certified', 'RoHS & REACH Compliant', 'Global Quality Standards', '20+ Brands Compatible'].map(item => (
                  <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <CheckCircle size={16} style={{ color: '#22c55e', flexShrink: 0, marginTop: 2 }} />
                    <span style={{
                      fontSize: 13.5, fontWeight: 500,
                      color: isDark ? '#d1d5db' : '#374151',
                      lineHeight: 1.5,
                    }}>{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/about" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '12px 22px', borderRadius: 12,
                background: 'linear-gradient(135deg, #dc2626, #991b1b)',
                color: '#fff', fontWeight: 700, fontSize: 14,
                textDecoration: 'none',
                boxShadow: '0 6px 20px rgba(220,38,38,0.25)',
                minHeight: 'unset', minWidth: 'unset',
              }}>
                Learn About Us
                <ChevronRight size={15} />
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <SectionSplitter theme={theme} variant="dots" />

      {/* ── Final CTA ── */}
      <section className="py-16 px-4">
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <AnimatedSection animation="scale-up">
            <div style={{
              borderRadius: 24, padding: 'clamp(28px, 5vw, 56px)',
              background: isDark
                ? 'linear-gradient(135deg, rgba(220,38,38,0.1), rgba(0,0,0,0.6))'
                : 'linear-gradient(135deg, #fff5f5, #fff)',
              border: isDark ? '2px solid rgba(220,38,38,0.2)' : '2px solid rgba(220,38,38,0.12)',
              boxShadow: isDark ? '0 30px 80px rgba(0,0,0,0.4)' : '0 20px 60px rgba(220,38,38,0.08)',
            }}>
              <h2 style={{
                fontSize: 'clamp(1.6rem, 4vw, 2.5rem)',
                fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 14,
                color: isDark ? '#f87171' : '#b91c1c',
              }}>
                Ready to Cut Your Connector Costs?
              </h2>
              <p style={{
                fontSize: '1.05rem', lineHeight: 1.75, marginBottom: 28,
                color: isDark ? '#d1d5db' : '#4b5563',
                maxWidth: 440, margin: '0 auto 28px',
              }}>
                Share your part number or current connector brand — we'll send you a detailed quote with pricing and compatibility confirmation within 24 hours.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
                <Link to="/contact" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '14px 30px', borderRadius: 14,
                  background: 'linear-gradient(135deg, #dc2626, #991b1b)',
                  color: '#fff', fontWeight: 700, fontSize: 16,
                  textDecoration: 'none',
                  boxShadow: '0 8px 28px rgba(220,38,38,0.35)',
                  minHeight: 'unset', minWidth: 'unset',
                }}>
                  Get a Free Quote
                  <ArrowRight size={16} />
                </Link>
                <Link to="/services" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '14px 24px', borderRadius: 14,
                  border: '2px solid rgba(220,38,38,0.35)',
                  color: isDark ? '#f87171' : '#dc2626',
                  fontWeight: 600, fontSize: 15,
                  textDecoration: 'none',
                  background: 'transparent',
                  minHeight: 'unset', minWidth: 'unset',
                }}>
                  Browse Services
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

    </div>
  );
};

export default Home;